/*
 * @Author: Mr.mjc
 * @Date: 2022-01-05 15:16:04
 * @LastEditors: MJC
 * @LastEditTime: 2025-11-06 10:48:57
 * @Description:
 * @FilePath: /next-ui/packages/wui-table/src/DragRowLine.tsx
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Event } from "./lib/utils";
import { columnType } from './constant';
import shallowequal from 'shallowequal';
import { IDragRowLineProps, IDragRowLineState } from './iTable';

const CAN_TOUCH = Event.isSupportTouch();

type GetTableRowDomsType = {
	centerTrs: HTMLCollectionOf<HTMLTableRowElement> | null | HTMLCollectionOf<HTMLTableRowElement> | NodeListOf<HTMLTableRowElement> | undefined | any,
	leftTrs: HTMLCollectionOf<HTMLTableRowElement> | null | HTMLCollectionOf<HTMLTableRowElement> | NodeListOf<HTMLTableRowElement> | undefined | any,
	rightTrs: HTMLCollectionOf<HTMLTableRowElement> | null | HTMLCollectionOf<HTMLTableRowElement> | NodeListOf<HTMLTableRowElement> | undefined | any
}

const DragRowLine: React.FC<IDragRowLineProps> = (props) => {
    const {
        clsPrefix = '',
        container,
        onRowDragStart,
        onRowDragDrop,
        useDragHandle = false,
        bigData = false,
        data,
        onRowDrop
    } = props;

    const [state, setState] = useState<IDragRowLineState>({
        visible: false,
        top: 0,
        left: 0,
        width: 0
    });

    const isTouchDragRowRef = useRef<boolean>(false);
    const contentTableRef = useRef<any>({});
    const prevDataRef = useRef<any>(data);

    const getTableRowDoms = useCallback((): GetTableRowDomsType | void | boolean => {
        if (!container || !clsPrefix) return false;
        let domElem = document.querySelector(`div[data-for-table="${container}"]`) as HTMLElement;
        if (!domElem) return false;
        let centerBody = domElem.querySelector(`.${clsPrefix}-scroll table tbody`);
        if (!centerBody) {
            centerBody = domElem.querySelector(`.${clsPrefix}-body table tbody`);
        }
        let leftBody = domElem.querySelector(`.${clsPrefix}-fixed-left table tbody`);
        let rightBody = domElem.querySelector(`.${clsPrefix}-scroll table tbody`);
        let centerTrs = centerBody && centerBody.getElementsByTagName("tr");
        let leftTrs = leftBody && leftBody.getElementsByTagName("tr");
        let rightTrs = rightBody && rightBody.getElementsByTagName("tr");
        return { centerTrs, leftTrs, rightTrs };
    }, [container, clsPrefix]);

    const initEvent = useCallback(() => {
        let trs = getTableRowDoms();
        if (!trs) return;
        let { centerTrs = [], leftTrs = [], rightTrs = [] } = trs as GetTableRowDomsType;
        let arr: any[] = [];
        let allTrs: Array<(HTMLElement)[]> = arr.concat(centerTrs, leftTrs, rightTrs);
        allTrs.forEach((trs: HTMLElement[]) => {
            Event.addHandlerArray(trs, 'dragstart', onDragStart);
            Event.addHandlerArray(trs, 'dragenter', onDragEnter);
            Event.addHandlerArray(trs, 'drop', onDrop);
            Event.addHandlerArray(trs, 'dragover', onDragOver);
            Event.addHandlerArray(trs, 'dragend', onDragEnd);
            if (CAN_TOUCH) {
                Event.addHandlerArray(trs, 'touchstart', onTouchStart);
                Event.addHandlerArray(trs, 'touchmove', onTouchMove);
                Event.addHandlerArray(trs, 'touchend', onTouchEnd);
            }
        });
    }, [getTableRowDoms]);

    const removeEvent = useCallback(() => {
        let trs = getTableRowDoms();
        if (!trs) return;
        let { centerTrs = [], leftTrs = [], rightTrs = [] } = trs as GetTableRowDomsType;
        let arr: any[] = [];
        let allTrs: Array<(HTMLElement)[]> = arr.concat(centerTrs, leftTrs, rightTrs);
        allTrs.forEach((trs: HTMLElement[]) => {
            Event.removeHandlerArray(trs, 'dragstart', onDragStart);
            Event.removeHandlerArray(trs, 'dragenter', onDragEnter);
            Event.removeHandlerArray(trs, 'drop', onDrop);
            Event.removeHandlerArray(trs, 'dragover', onDragOver);
            Event.removeHandlerArray(trs, 'dragend', onDragEnd);
            if (CAN_TOUCH) {
                Event.removeHandlerArray(trs, 'touchstart', onTouchStart);
                Event.removeHandlerArray(trs, 'touchmove', onTouchMove);
                Event.removeHandlerArray(trs, 'touchend', onTouchEnd);
            }
        });
    }, [getTableRowDoms]);

    const onDragStart = useCallback((e: React.DragEvent<HTMLElement>, touchTarget?: React.ReactNode) => {
        let event = Event.getEvent(e);
        let target = touchTarget ? touchTarget : Event.getTarget(event);
        if (useDragHandle) {
            if (target.nodeName.toUpperCase() !== 'TD' || target.attributes.getNamedItem('columntype')?.value !== columnType.ROWDRAG) {
                return;
            }
        }
        if (target.nodeName.toUpperCase() == 'TD') target = target.parentNode;
        if (target.nodeName.toUpperCase() !== "TR") return;
        let dragStartKey = target.getAttribute("data-row-key");
        let dragStartIndex = target.getAttribute("data-row-index");
        if (!touchTarget) {
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("Text", JSON.stringify({ dragStartKey, dragStartIndex }));
        }
        contentTableRef.current.dragStartKey = dragStartKey;
        contentTableRef.current.dragStartIndex = dragStartIndex;
        onRowDragStart && onRowDragStart({ dragStartKey, dragStartIndex });
    }, [useDragHandle, onRowDragStart]);

    const onDragEnter = useCallback((e: React.DragEvent<HTMLElement>, touchTarget?: ParentNode | null) => {
        let event = Event.getEvent(e),
            _target = Event.getTarget(event);
        let target = touchTarget ? touchTarget : _target.parentNode;
        if (target.nodeName.toUpperCase() == 'TD') target = target.parentNode;
        if (target.nodeName.toUpperCase() !== "TR") return;
        if (!contentTableRef.current) return;
        let dragEnterKey = target.getAttribute("data-row-key");
        let dragEnterIndex = target.getAttribute("data-row-index");
        if (!dragEnterKey) return;
        let dropAlign = parseInt(dragEnterIndex) >= parseInt(contentTableRef.current.dragStartIndex) + 1 ? 'bottom' : 'top';
        if (contentTableRef.current.dragStartKey && dragEnterKey !== contentTableRef.current.dragStartKey) {
            contentTableRef.current.dragEnterKey = dragEnterKey;
            contentTableRef.current.dragEnterIndex = dragEnterIndex;
            setLinePosition(contentTableRef.current, target, dropAlign);
        } else {
            contentTableRef.current.dragEnterKey = null;
            contentTableRef.current.dragEnterIndex = null;
            setLinePosition(contentTableRef.current, null, null);
        }
    }, []);

    const onDrop = useCallback((e: React.DragEvent<HTMLElement>, touchTarget?: ParentNode | null) => {
        let event = Event.getEvent(e),
            _target = Event.getTarget(event);
        let target = touchTarget ? touchTarget : _target.parentNode;
        let dataIndex;
        while (_target.nodeName.toUpperCase() !== 'TD') {
            _target = _target.parentNode;
        }
        dataIndex = _target.getAttribute("data-index");

        if (target.nodeName.toUpperCase() == 'TD') target = target.parentNode;
        if (target.nodeName.toUpperCase() !== "TR") return;
        if (!contentTableRef.current) return;
        let dragEnterKey = target.getAttribute("data-row-key");
        let dragEnterIndex = target.getAttribute("data-row-index");
        onRowDrop && onRowDrop({ dragEnterKey, dragEnterIndex, dataIndex, e });
    }, [onRowDrop]);

    const onDragEnd = useCallback((e: React.DragEvent<HTMLElement>) => {
        if (!contentTableRef.current) return;
        let { dragStartKey, dragStartIndex, dragEnterKey, dragEnterIndex } = contentTableRef.current || {};
        onRowDragDrop && onRowDragDrop({
            dragTargetKey: dragStartKey,
            dragTargetIndex: dragStartIndex,
            dropTargetKey: dragEnterKey,
            dropTargetIndex: dragEnterIndex,
            event: e
        });
        contentTableRef.current.dragStartKey = null;
        contentTableRef.current.dragStartIndex = null;
        contentTableRef.current.dragEnterKey = null;
        contentTableRef.current.dragEnterIndex = null;
        setLinePosition(null, null, null);
    }, [onRowDragDrop]);

    const onDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
        let event = Event.getEvent(e);
        event.preventDefault();
    }, []);

    const onTouchStart = useCallback((e: React.DragEvent<HTMLElement>) => {
        e.stopPropagation();
        let event = Event.getEvent(e),
            _target = Event.getTarget(event);
        let target = _target;
        let maxCount = 0;
        while (target.tagName !== 'TR') {
            target = target.parentNode;
            maxCount++;
            if (target.tagName == 'TABLE' || target.tagName == 'BODY' || maxCount > 10) {
                break;
            }
        }
        if (target.tagName === 'TR') {
            isTouchDragRowRef.current = true;
            onDragStart(e, target);
        } else {
            isTouchDragRowRef.current = false;
        }
    }, [onDragStart]);

    const onTouchMove = useCallback((e: React.DragEvent<HTMLElement>) => {
        if (!isTouchDragRowRef.current) return;
        e.stopPropagation();
        let event = Event.getEvent(e);
        event.preventDefault();
        let touchTarget = getTouchDom(event) as HTMLElement,
            target = touchTarget.parentNode;
        onDragEnter(e, target);
    }, [onDragEnter]);

    const onTouchEnd = useCallback((e: React.DragEvent<HTMLElement>) => {
        if (!isTouchDragRowRef.current) return;
        e.stopPropagation();
        onDragEnd(e);
        isTouchDragRowRef.current = false;
    }, [onDragEnd]);

    const getTouchDom = useCallback((event: React.TouchEvent) => {
        let currentLocation = event.changedTouches[0];
        let realTarget = document.elementFromPoint(currentLocation.clientX, currentLocation.clientY);
        return realTarget;
    }, []);

    const setLinePosition = useCallback((targetContainer: HTMLElement | null, targetDom: HTMLElement | null, targetAlign: string | null) => {
        if (!targetContainer || !targetDom) {
            setState({ left: 0, top: 0, width: 0, visible: false });
        } else {
            let domElem = document.querySelector(`div[data-for-table="${container}"]`) as HTMLElement;
            if (!domElem) return;
            let contRect = domElem.getBoundingClientRect();
            let centerBodyDom = domElem.querySelector(`.${clsPrefix}-content .${clsPrefix}-body`) as HTMLElement;
            let centerBodyRect = centerBodyDom.getBoundingClientRect();
            let headerHeight = centerBodyRect.top - contRect.top;
            let targetRect = (targetDom as HTMLElement).getBoundingClientRect();
            let top = headerHeight + targetRect.top - centerBodyRect.top - 1;
            if (targetAlign && ['bottom', 'down'].includes(targetAlign)) {
                top = top + targetRect.height;
            }
            setState({ left: 0, width: contRect.width, top, visible: true });
        }
    }, [container, clsPrefix]);

    useEffect(() => {
        if (!bigData && container) {
            initEvent();
        }
        return () => {
            removeEvent();
        };
    }, [bigData, container, initEvent, removeEvent]);

    // 在bigData存在、数据变化或container变化时，重新执行removeEvent()和initEvent()
    useEffect(() => {
        const isDataChanged = !shallowequal(prevDataRef.current, data);
        if (bigData || isDataChanged || container) {
            removeEvent();
            initEvent();
            prevDataRef.current = data;
        }
    }, [bigData, data, container, removeEvent, initEvent]);

    const { left, width, top, visible } = state;
    let style = {
        position: 'absolute' as const,
        left: left + 'px',
        top: top + 'px',
        height: '2px',
        width: width + 'px',
        background: '#02B1FD',
        zIndex: 'auto' as const,
        display: visible ? 'block' : 'none'
    };

    return (
        <div style={style} className={`${clsPrefix}-drag-row-line`}
            dangerouslySetInnerHTML={{ __html: '&nbsp;' }}></div>
    );
};

export default DragRowLine;
